import levenshtein from 'fast-levenshtein';

import {
  bracketedPattern,
  exactMatchDistanceThreshold,
  fuzzyMatchDistanceThreshold,
  standaloneConfigForGreetings,
  templateVariables,
  variableAliases
} from './formatting_constants';

export const createCanonicalVariables = () =>
  Object.keys(templateVariables).reduce((acc, key) => {
    acc[key] = `{{${key}}}`;
    return acc;
  }, {});

export const cleanInputVariable = inputVar =>
  inputVar
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_\s]/g, '')
    .replace(/\s+/g, '_');

export const findExactMatch = cleanInput => (
  Object.entries(variableAliases).find(
    ([canonical, aliases]) => canonical.toLowerCase() === cleanInput || aliases.some(alias => alias.replace(/\s+/g, '_') === cleanInput)
  )?.[0] || null
);

export const findFuzzyMatch = cleanInput => {
  const allMatches = Object.entries(variableAliases)
    .flatMap(([canonical, aliases]) => [
      {
        canonical,
        editDistance: levenshtein.get(cleanInput, canonical.toLowerCase()),
        isCanonical: true,
        matchedText: canonical.toLowerCase()
      },
      ...aliases.map(alias => {
        const aliasClean = alias.replace(/\s+/g, '_');
        return {
          canonical,
          editDistance: levenshtein.get(cleanInput, aliasClean),
          isCanonical: false,
          matchedText: aliasClean
        };
      })
    ])
    .filter(({ isCanonical, editDistance }) => editDistance <= (isCanonical ? exactMatchDistanceThreshold : fuzzyMatchDistanceThreshold))
    .sort((a, b) => a.editDistance - b.editDistance);

  if (allMatches.length === 0) return null;

  const minDistance = allMatches[0].editDistance;

  // Prefer canonical matches in ties, otherwise keep first
  return allMatches
    .filter(match => match.editDistance === minDistance)
    .reduce((best, current) => (!best || (current.isCanonical && !best.isCanonical) ? current : best), null).canonical;
};

export const findBestMatch = inputVar => {
  const cleanInput = cleanInputVariable(inputVar);
  return findExactMatch(cleanInput) || findFuzzyMatch(cleanInput);
};

export const isAlreadyCorrect = ({ match, type, canonicalVariables }) => type === 'double' && Object.values(canonicalVariables).includes(match);

export const processPatternMatch = ({ match, variableName, type, canonicalVariables }) => {
  if (!variableName || isAlreadyCorrect({ match, type, canonicalVariables })) return match;

  const bestMatch = findBestMatch(variableName);
  return (bestMatch && canonicalVariables[bestMatch]) || match;
};

export const processTemplatePatterns = (message, templatePatterns, canonicalVariables) =>
  templatePatterns.reduce(
    (normalizedMessage, { pattern, type }) =>
      normalizedMessage.replace(pattern, (match, variableName) => processPatternMatch({ match, variableName, type, canonicalVariables })),
    message
  );

// Process single braces that are not part of double/triple braces
export const processSingleBraces = (message, canonicalVariables) => {
  const bracketedPattern = /\{\s*([A-Za-z_][A-Za-z0-9_\s]*?)\s*\}/gi;
  
  return message.replace(bracketedPattern, (match, variableName, offset) => {
    const precedingChar = offset > 0 ? message[offset - 1] : '';
    const nextIndex = offset + match.length;
    const nextChar = nextIndex < message.length ? message[nextIndex] : '';

    // Skip if this is part of a double/triple brace
    if (precedingChar === '{' || nextChar === '}') {
      return match;
    }

    // Check for exact match in canonical variables
    if (Object.keys(canonicalVariables).includes(variableName)) {
      return canonicalVariables[variableName];
    }

    const cleanInput = cleanInputVariable(variableName);
    const bestMatch = findExactMatch(cleanInput) || findFuzzyMatch(cleanInput);
    return (bestMatch && canonicalVariables[bestMatch]) || match;
  });
};

export const processStandalonePatterns = message =>
  standaloneConfigForGreetings.reduce((result, { pattern, replacement }) => {
    // "{salutation} {talent}" patterns
    const greetingPattern = new RegExp(`\\b(hi|hello|dear|hey)\\s+${pattern.source}\\b`, 'gi');
    const afterGreeting = result.replace(greetingPattern, match => match.replace(pattern, replacement));
    
    // "{salutation} from {brand}" patterns
    const fromPattern = new RegExp(`\\b(hi|hello|dear|hey)\\s+from\\s+${pattern.source}\\b`, 'gi');
    return afterGreeting.replace(fromPattern, match => match.replace(pattern, replacement));
  }, message);
